import { DatePipe } from '@angular/common';
import { Component, computed, effect, forwardRef, HostListener, input, output, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { format, getDaysInMonth, isAfter, isBefore, isSameDay } from 'date-fns';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';

type DateString = `${string}-${string}-${string}`;

@Component({
  selector: 'app-calendar-picker',
  imports: [FormsModule, DatePipe, LoadingSpinnerComponent],
  standalone: true,
  templateUrl: './calendar-picker.component.html',
  styleUrl: './calendar-picker.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => CalendarPickerComponent),
    },
  ],
})
export class CalendarPickerComponent implements ControlValueAccessor {
  disabledDates = input<string[]>([]);

  activeDates = input<string[]>([]);

  availabilityStartDate = input<DateString | undefined>();

  onlyShowActiveDatesInCurrentMonth = input<boolean>(false);

  maxDate = input<DateString | undefined | Date>();

  minDate = input<DateString | undefined>();

  loading = input<boolean>();

  todaysDate = input<DateString | string>();

  // added this because if the date array is empty on booking page it shows all slots as availiable
  allowEmptyDates = input<boolean>(false);

  readonly label = input<string | undefined>();
  readonly name = input.required<string>();

  showTodaySelection = input<boolean>(false);
  checkedTodaysDate = signal(false);
  internalValue: DateString | null | string | any = null;

  showShortenedDays = input<boolean>(false);

  currentMonthAndYear = output<string>();

  onCheckboxChanged(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.checkedTodaysDate.set(checked);

    if (checked) {
      this.internalValue = this.formattedTodaysDate;
      this.writeValue(this.internalValue);
    } else {
      this.internalValue = null;
      this.writeValue(undefined);
    }
  }
  previouslyFocusedDay: HTMLButtonElement | undefined;

  clearFocusState(): void {
    this.previouslyFocusedDay?.classList?.remove('focus');
  }

  @HostListener('document:click', ['$event'])
  handleOutsideClick(event: MouseEvent) {
    if ((event.srcElement as HTMLElement).closest('app-calendar-picker')) return;
    this.inCalendarView = false;
    this.clearFocusState();
  }
  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (['ArrowRight', 'ArrowUp', 'ArrowDown', 'ArrowLeft'].includes(event.key)) {
      event.preventDefault();
    }
    setTimeout(() => {
      // Don't run if outside this component
      if (!document.activeElement?.closest('app-calendar-picker')) {
        this.clearFocusState();
        this.inCalendarView = false;
        return;
      }

      if (['ArrowRight', 'ArrowUp', 'ArrowDown', 'ArrowLeft'].includes(event.key)) {
        const { day, week } = this.getNewDayAndWeek(event.key);
        this.focussedDay = day;
        this.focusedWeek = week;

        setTimeout(() => {
          const weekSelection = document.querySelectorAll('.week-holder .week')[this.focusedWeek];
          const daySelection = weekSelection.querySelectorAll('.day')[this.focussedDay] as HTMLButtonElement;

          const hasDayClass = document.activeElement?.classList.contains('day');
          this.inCalendarView = !!hasDayClass;

          this.clearFocusState();
          if (daySelection) {
            daySelection.classList.add('focus');
            daySelection.focus();
            this.previouslyFocusedDay = daySelection;
          }
        });
      }

      if (event.key === 'Tab') {
        if (this.inCalendarView) {
          this.inCalendarView = false;
          return;
        }

        const weekSelection = document.querySelectorAll('.week-holder .week')[this.focusedWeek];
        const daySelection = weekSelection.querySelectorAll('.day')[this.focussedDay] as HTMLButtonElement;
        const hasDayClass = document.activeElement?.classList.contains('day');

        if (daySelection && hasDayClass) {
          daySelection.focus();
          daySelection.classList.add('focus');
          this.previouslyFocusedDay = daySelection;
          this.inCalendarView = true;
        }
      }
    });
  }

  private viewModel = computed(() => {
    const currentMonthAndYear = `${new Date().getFullYear()}_${new Date().getMonth()}`;

    const selectedMonthAndYear = `${this.selectedYear()}_${this.selectedMonth()}`;

    const formattedMonth = this.selectedMonth() + 1 < 10 ? `0${this.selectedMonth() + 1}` : this.selectedMonth() + 1;

    let formattedMaxMonthAndYear;

    if (this.maxDate()) {
      const maxDateFormatted = new Date(this.maxDate() as string);
      formattedMaxMonthAndYear = `${maxDateFormatted.getFullYear()}_${maxDateFormatted.getMonth()}`;
    }

    let formattedMinMonthAndYear;

    if (this.minDate()) {
      const minDateFormatted = new Date(this.minDate() as string);
      formattedMinMonthAndYear = `${minDateFormatted.getFullYear()}_${minDateFormatted.getMonth()}`;
    }

    return {
      canGoForward: !this.maxDate() || selectedMonthAndYear !== formattedMaxMonthAndYear,
      currentMonthAndYear,
      canGoBackwards: !this.minDate() || selectedMonthAndYear !== formattedMinMonthAndYear,
      displayMonth: this.months[this.selectedMonth()],
      showSelectedYear: this.selectedYear(),
      selectedYear: this.selectedYear(),
      selectedMonth: this.selectedMonth(),
      weekInMonth: this.getDatesArray(),
      daysInMonth: new Array(getDaysInMonth(new Date(this.selectedYear(), this.selectedMonth())))
        .fill('')
        .map((_, index) => `${this.selectedYear()}-${formattedMonth}-${index + 1 < 10 ? `0${index + 1}` : index + 1}`),
      showTodaySelection: this.showTodaySelection(),
      checkedTodaysDate: this.checkedTodaysDate(),
      loading: this.loading(),
    };
  });

  get vm() {
    return this.viewModel();
  }

  touched = false;

  disabled = false;

  value: string | undefined;

  *chunks<T>(arr: T[], n: number) {
    for (let i = 0; i < arr.length; i += n) {
      yield arr.slice(i, i + n);
    }
  }

  getNewDayAndWeek(keyEvent: string): { day: number; week: number } {
    let targetDay = this.focussedDay;
    let targetWeek = this.focusedWeek;

    if (keyEvent === 'ArrowRight') {
      targetDay = targetDay + 1;

      if (targetDay === 7) {
        targetWeek = targetWeek + 1;
      }
    } else if (keyEvent === 'ArrowLeft') {
      targetDay = targetDay - 1;

      if (targetDay === -1) {
        targetWeek = targetWeek - 1;
      }
    } else if (keyEvent === 'ArrowUp') {
      targetWeek = targetWeek - 1;
    } else if (keyEvent === 'ArrowDown') {
      targetWeek = targetWeek + 1;
    }

    if (targetDay === 7) {
      if (targetWeek === this.vm.weekInMonth.length && this.vm.canGoForward) {
        targetDay = 0;
        targetWeek = 1;
        this.moveMonthForward();
      } else if (targetWeek === this.vm.weekInMonth.length && !this.vm.canGoForward) {
        targetDay = 6;
      } else {
        targetDay = 0;
      }
    }
    if (targetWeek === this.vm.weekInMonth.length) {
      targetWeek = this.vm.canGoForward ? 1 : this.vm.weekInMonth.length - 1;
      if (this.vm.canGoForward) {
        this.moveMonthForward();
      }
    }

    if (targetWeek === -1) {
      const canGoBack = this.vm.canGoBackwards;
      if (this.vm.canGoBackwards) {
        this.moveMonthBack();
      }
      // Just use 2 here so that it skips the week
      targetWeek = canGoBack ? this.vm.weekInMonth.length - 2 : 0;
    }

    if (targetDay === -1) {
      if (targetWeek === 0 && this.focusedWeek === 0 && !this.vm.canGoBackwards) {
        targetDay = 0;
      } else {
        targetDay = 6;
      }
    }
    return {
      day: targetDay,
      week: targetWeek,
    };
  }

  getDatesArray(): { date: string; isCurrentMonth: boolean; isActive: boolean }[][] {
    const previousMonth = this.selectedMonth() === 0 ? 11 : this.selectedMonth() - 1;
    const previousYear = previousMonth === 11 ? this.selectedYear() - 1 : this.selectedYear();

    const nextMonth = this.selectedMonth() === 11 ? 0 : this.selectedMonth() + 1;
    const nextYear = nextMonth === 0 ? this.selectedYear() + 1 : this.selectedYear();

    const getDaysInMonthArray = (year: number, month: number, isCurrentMonth = false) => {
      const formattedMonth = month + 1 < 10 ? `0${month + 1}` : month + 1;

      return new Array(getDaysInMonth(new Date(year, month))).fill('').map((_, index) => {
        const formattedDay = index + 1 < 10 ? `0${index + 1}` : index + 1;

        const formattedDate = `${year}-${formattedMonth}-${formattedDay}`;
        const dateNotInDisabledList = !this.disabledDates().some((i) => i.startsWith(formattedDate));
        const dateInActiveList = this.allowEmptyDates() || this.activeDates().some((i) => i.startsWith(formattedDate));
        const currentMonthValidCheck = !this.onlyShowActiveDatesInCurrentMonth() || isCurrentMonth;
        const isDateAfterMinDate =
          !this.minDate() ||
          isAfter(new Date(formattedDate), new Date(this.minDate() as string)) ||
          isSameDay(new Date(formattedDate), new Date(this.minDate() as string));
        const isDateBeforeMaxDate =
          !this.maxDate() ||
          isBefore(new Date(formattedDate), new Date(this.maxDate() as string)) ||
          isSameDay(new Date(formattedDate), new Date(this.maxDate() as string));

        return {
          date: `${year}-${formattedMonth}-${formattedDay}`,
          isCurrentMonth,
          isActive:
            dateInActiveList &&
            dateNotInDisabledList &&
            currentMonthValidCheck &&
            isDateAfterMinDate &&
            isDateBeforeMaxDate,
        };
      });
    };

    let daysInPreviousMonth = getDaysInMonthArray(previousYear, previousMonth);
    const daysInNextMonth = getDaysInMonthArray(nextYear, nextMonth);
    const daysInCurrentMonth = getDaysInMonthArray(this.selectedYear(), this.selectedMonth(), true);
    const weekDayOfFirstDate = new Date(daysInCurrentMonth[0].date).getDay();
    const numberOfDaysToTakeOFPreviousMonth = weekDayOfFirstDate === 0 ? 6 : weekDayOfFirstDate - 1;

    daysInPreviousMonth = daysInPreviousMonth.slice(
      daysInPreviousMonth.length - numberOfDaysToTakeOFPreviousMonth,
      daysInPreviousMonth.length
    );

    const numberOfWeeksToTake = [...daysInPreviousMonth, ...daysInCurrentMonth].length > 35 ? 42 : 35;

    const numberOfDaysToTakeOfNextMonth = numberOfWeeksToTake - [...daysInPreviousMonth, ...daysInCurrentMonth].length;

    return [
      ...this.chunks(
        [...daysInPreviousMonth, ...daysInCurrentMonth, ...daysInNextMonth.slice(0, numberOfDaysToTakeOfNextMonth)],
        7
      ),
    ];
  }

  inCalendarView = false;

  focussedDay = 0;
  focusedWeek = 0;
  allowFutureDates = input<boolean>();

  selectedMonth = signal(new Date().getMonth());
  selectedYear = signal(new Date().getFullYear());

  private readonly lastAlignedAvailability = signal<DateString | undefined>(undefined);
  private readonly selectedDateSignal = signal<string | undefined>(undefined);

  private readonly alignCalendarToAvailability = effect(() => {
    const availabilityStartDate = this.availabilityStartDate();
    const previouslyAlignedAvailability = this.lastAlignedAvailability();
    const hasSelection = !!this.selectedDateSignal();

    if (!availabilityStartDate) {
      if (previouslyAlignedAvailability) {
        this.lastAlignedAvailability.set(undefined);
      }
      return;
    }

    if (hasSelection) {
      return;
    }

    if (previouslyAlignedAvailability === availabilityStartDate) {
      return;
    }

    const [targetYearString, targetMonthString] = availabilityStartDate.split('-');
    const targetYear = Number.parseInt(targetYearString, 10);
    const targetMonth = Number.parseInt(targetMonthString, 10) - 1;

    if (Number.isNaN(targetYear) || Number.isNaN(targetMonth)) {
      this.lastAlignedAvailability.set(availabilityStartDate);
      return;
    }

    const alreadyDisplayingTargetMonth = this.selectedMonth() === targetMonth && this.selectedYear() === targetYear;

    if (!alreadyDisplayingTargetMonth) {
      this.selectedMonth.set(targetMonth);
      this.selectedYear.set(targetYear);
      this.currentMonthAndYear.emit(`${targetYear}_${targetMonth + 1}`);
    }

    this.lastAlignedAvailability.set(availabilityStartDate);
  });

  months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  daysInWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  shortDatesInWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  get daysToDisplay() {
    return this.showShortenedDays() ? this.shortDatesInWeek : this.daysInWeek;
  }

  moveMonthBack(): void {
    if (this.vm.selectedMonth === 0) {
      this.selectedMonth.set(11);
      this.selectedYear.set(this.selectedYear() - 1);
      return;
    }

    this.selectedMonth.set(this.selectedMonth() - 1);
    this.currentMonthAndYear.emit(`${this.vm.selectedYear}_${this.vm.selectedMonth + 1}`);
  }

  moveMonthForward(): void {
    if (this.vm.selectedMonth === 11) {
      this.selectedMonth.set(0);
      this.selectedYear.set(this.selectedYear() + 1);
      return;
    }

    this.selectedMonth.set(this.selectedMonth() + 1);
    this.currentMonthAndYear.emit(`${this.vm.selectedYear}_${this.vm.selectedMonth + 1}`);
  }

  get formattedTodaysDate() {
    return format(new Date(), 'yyy-MM-dd');
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChange: (value: any) => void = (value: any) => {};

  onTouched = () => {};

  writeValue(value: any) {
    this.value = value;
    this.selectedDateSignal.set(value ?? undefined);
    if (value) {
      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) {
        const targetMonth = parsed.getMonth();
        const targetYear = parsed.getFullYear();
        if (this.selectedMonth() !== targetMonth || this.selectedYear() !== targetYear) {
          this.selectedMonth.set(targetMonth);
          this.selectedYear.set(targetYear);
          this.currentMonthAndYear.emit(`${targetYear}_${targetMonth + 1}`);
        }
      }
    }
    this.onChange!(this.value);
    this.clearFocusState();
  }

  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  getCalendarDayAriaLabel(day: any): string {
    const date = new Date(day.date);
    const dayOfWeek = format(date, 'EEEE');
    const month = format(date, 'MMMM');
    const dayOfMonth = date.getDate();
    const year = date.getFullYear();

    let label = `${dayOfWeek}, ${month} ${dayOfMonth}, ${year}`;

    if (!day.isActive) {
      label += ', unavailable';
    } else if (this.value === day.date) {
      label += ', selected';
    } else {
      label += ', available';
    }

    return label;
  }
}
